/**
 * Low-level HTML helpers for the diff renderer.
 *
 * Extracted from renderDiffTree.ts to keep that file focused on rendering logic.
 * ZERO product imports - only cheerio and domhandler types.
 */
import * as cheerio from 'cheerio';
import type { Element as DomElement } from 'domhandler';

// ---------------------------------------------------------------------------
// Escaping
// ---------------------------------------------------------------------------

export const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export const renderAttribute = (name: string, value: string | null | undefined): string =>
  value ? ` ${name}="${escapeHtml(value)}"` : '';

// ---------------------------------------------------------------------------
// Cheerio context helpers
// ---------------------------------------------------------------------------

/**
 * Wraps inner HTML with the appropriate context for parsing fragment elements
 * like <tr>, <td>, <li> that require a parent context.
 */
export const getSerializationContext = (html: string): { wrapper: string; selector: string } => {
  const tagName = /^<\s*([a-z0-9-]+)/i.exec(html)?.[1]?.toLowerCase();
  switch (tagName) {
    case 'tbody':
    case 'thead':
    case 'tfoot':
    case 'caption':
    case 'colgroup':
      return { wrapper: `<table>${html}</table>`, selector: `table > ${tagName}` };
    case 'tr':
      return { wrapper: `<table><tbody>${html}</tbody></table>`, selector: 'table > tbody > tr' };
    case 'td':
    case 'th':
      return { wrapper: `<table><tbody><tr>${html}</tr></tbody></table>`, selector: `table > tbody > tr > ${tagName}` };
    case 'li':
      return { wrapper: `<ul>${html}</ul>`, selector: 'ul > li' };
    default:
      return { wrapper: html, selector: 'body > *' };
  }
};

export const parseSerializedHtml = (
  html: string
): { $: cheerio.CheerioAPI; $el: cheerio.Cheerio<DomElement> } | null => {
  const { wrapper, selector } = getSerializationContext(html);
  const $ = cheerio.load(wrapper);
  const $el = $(selector).first() as cheerio.Cheerio<DomElement>;
  return $el.length ? { $, $el } : null;
};

// ---------------------------------------------------------------------------
// Data-style hydration
// ---------------------------------------------------------------------------

/** Map of data-* attributes to CSS property names for visual hydration in changes view. */
export const DATA_STYLE_MAP: Readonly<Record<string, string>> = {
  'data-background-color': 'background-color',
};

export const hydrateElementDataStyles = (el: DomElement | undefined): void => {
  if (!el) return;
  for (const [dataAttr, cssProp] of Object.entries(DATA_STYLE_MAP)) {
    const val = el.attribs[dataAttr];
    if (val && !(el.attribs.style || '').includes(cssProp)) {
      el.attribs.style = `${el.attribs.style ? el.attribs.style + '; ' : ''}${cssProp}: ${val}`;
    }
  }
};

/** Add inline styles from known data-* attributes so colors render in view mode. */
export const hydrateDataStyles = (attribs: Record<string, string>): Record<string, string> => {
  const attrs = { ...attribs };
  for (const [dataAttr, cssProp] of Object.entries(DATA_STYLE_MAP)) {
    if (attrs[dataAttr] && !attrs.style?.includes(cssProp)) {
      attrs.style = `${attrs.style ? attrs.style + '; ' : ''}${cssProp}: ${attrs[dataAttr]}`;
    }
  }
  return attrs;
};

// ---------------------------------------------------------------------------
// Element serialization
// ---------------------------------------------------------------------------

export const replaceInnerHtml = (outerHtml: string, innerHtml: string): string => {
  const parsed = parseSerializedHtml(outerHtml);
  if (!parsed) return innerHtml;
  const { $, $el } = parsed;
  hydrateElementDataStyles($el.get(0));
  if ($el.get(0)?.tagName === 'table') {
    // Preserve colgroups if not already in the inner HTML
    const preservedColgroups = /<\s*colgroup[\s>]/i.test(innerHtml)
      ? ''
      : $el
          .children('colgroup')
          .toArray()
          .map((cg) => $.html(cg))
          .join('');
    $el.html(`${preservedColgroups}${innerHtml}`);
    return $.html($el);
  }
  $el.html(innerHtml);
  return $.html($el);
};

/** Tags that require Cheerio parsing due to HTML context requirements or special logic. */
export const CHEERIO_REQUIRED_TAGS = new Set([
  'table',
  'tbody',
  'thead',
  'tfoot',
  'caption',
  'colgroup',
  'tr',
  'td',
  'th',
  'li',
]);

/** String-based element reconstruction - avoids Cheerio for simple elements. */
export const buildElementHtml = (el: DomElement, innerHTML: string): string => {
  const hydrated = hydrateDataStyles(el.attribs);
  const attrs = Object.entries(hydrated)
    .map(([k, v]) => ` ${k}="${escapeHtml(v)}"`)
    .join('');
  return `<${el.tagName}${attrs}>${innerHTML}</${el.tagName}>`;
};

export const addClass = (html: string, className: string): string => {
  const match = /^(<[a-z0-9-]+\s[^>]*?\bclass=")([^"]*)/i.exec(html);
  if (match) {
    return `${html.slice(0, match[1].length)}${match[2]} ${className}"${html.slice(match[1].length + match[2].length + 1)}`;
  }
  // Insert class attribute just before the closing `>` of the opening tag.
  return html.replace(/^(<[a-z0-9-]+(?:\s[^>]*)?)>/i, `$1 class="${className}">`);
};
